import MarkdownItFactory from "markdown-it";
import type { MarkdownIt, Token } from "markdown-it";
import { Fragment, Mark, Node as ProseMirrorNode } from "prosemirror-model";

import {
  IMAGE_LAYOUT_CLASSES,
  MarkName,
  NodeName,
  NOTICE_STYLES,
  markType,
  nodeType,
  schema,
  type CellAlignment,
  type ImageLayoutClass,
  type MentionType,
  type NoticeStyle,
} from "../schema/index.js";
import { isFileHref, parseLinkText } from "./media.js";
import {
  ESCAPED_BACKSLASH,
  containerPlugin,
  emojiPlugin,
  escapedBackslashPlugin,
  highlightPlugin,
  inlineMathPlugin,
  mathBlockPlugin,
  mentionPlugin,
  placeholderPlugin,
  underlinePlugin,
} from "./plugins.js";
import { preprocessAlphaLists, type AlphaListMarker } from "./preprocess.js";

const TASK_MARKER = /^\[([ xX_-])\]\s+(?=\S)/;
const HARD_BREAK_MARKER = "\uE000br\uE000";

function createParser(generateId: () => string): MarkdownIt {
  const md = new MarkdownItFactory("default", { html: false, linkify: false, breaks: false });
  md.use(escapedBackslashPlugin);
  md.use(highlightPlugin);
  md.use(placeholderPlugin);
  md.use(inlineMathPlugin);
  md.use(emojiPlugin);
  md.use(mathBlockPlugin);
  md.use((instance) => {
    containerPlugin(instance, "notice", ":");
  });
  md.use((instance) => {
    containerPlugin(instance, "toggle", "+");
  });
  md.use(underlinePlugin);
  md.use((instance) => {
    mentionPlugin(instance, generateId);
  });
  return md;
}

function defaultId(): string {
  return globalThis.crypto.randomUUID();
}

const parser = createParser(defaultId);

/* ------------------------------------------------------------------ tokens */

interface TokenNode {
  token: Token;
  children: TokenNode[];
}

function buildTree(tokens: Token[]): TokenNode[] {
  const root: TokenNode[] = [];
  const stack: TokenNode[][] = [root];
  for (const token of tokens) {
    const target = stack[stack.length - 1];
    if (!target) continue;
    if (token.nesting === 1) {
      const node: TokenNode = { token, children: [] };
      target.push(node);
      stack.push(node.children);
    } else if (token.nesting === -1) {
      if (stack.length > 1) stack.pop();
    } else {
      // Inline tokens carry their own nested token stream.
      target.push({ token, children: token.children ? buildTree(token.children) : [] });
    }
  }
  return root;
}

/** The first inline token of a list item, used for task-marker detection. */
function itemLead(tokens: Token[], itemIndex: number): Token | null {
  for (let i = itemIndex + 1; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (!token) break;
    if (token.type === "inline") return token;
    if (token.type === "paragraph_open" || token.type === "paragraph_close") continue;
    break;
  }
  return null;
}

/**
 * Reproduces the reference's backwards task-list promotion: items are examined
 * last-to-first and each claims the nearest unclaimed list opening at its own
 * nesting level. An unrelated earlier list can therefore be hijacked into a
 * task list — a defect we match so documents round-trip identically.
 */
function claimTaskLists(tokens: Token[]): Set<number> {
  const claimed = new Set<number>();
  const taskItems: number[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token?.type !== "list_item_open") continue;
    const lead = itemLead(tokens, i);
    if (lead && TASK_MARKER.test(lead.content)) taskItems.push(i);
  }
  // Opens grouped by nesting level, ascending. Because task items are visited
  // in decreasing document order, one descending cursor per level finds each
  // "nearest unclaimed opening" in linear time.
  const opensByLevel = new Map<number, number[]>();
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token?.type !== "bullet_list_open" && token?.type !== "ordered_list_open") continue;
    const opens = opensByLevel.get(token.level);
    if (opens) opens.push(i);
    else opensByLevel.set(token.level, [i]);
  }

  const cursors = new Map<number, number>();
  for (let k = taskItems.length - 1; k >= 0; k -= 1) {
    const itemIndex = taskItems[k];
    if (itemIndex === undefined) continue;
    const item = tokens[itemIndex];
    if (!item) continue;
    const opens = opensByLevel.get(item.level - 1);
    if (!opens) continue;
    let cursor = cursors.get(item.level - 1) ?? opens.length - 1;
    while (cursor >= 0 && (opens[cursor] ?? -1) >= itemIndex) cursor -= 1;
    if (cursor >= 0) {
      claimed.add(opens[cursor] ?? -1);
      cursor -= 1;
    }
    cursors.set(item.level - 1, cursor);
  }
  return claimed;
}

/* -------------------------------------------------------------- conversion */

interface Context {
  alphaMarkers: Map<number, AlphaListMarker>;
  checkboxLists: Set<Token>;
}

/**
 * Builds a node from parsed children. Content is coerced to the content
 * expression: required leading children are filled in, but a node holding a
 * child that cannot be placed at all is dropped whole rather than repaired —
 * the reference behaves the same way, and `interaction-list-in-notice-in-toggle`
 * pins it.
 */
function build(
  name: string,
  attrs: Record<string, unknown> | null,
  children: ProseMirrorNode[],
): ProseMirrorNode | null {
  return nodeType(name).createAndFill(attrs, Fragment.from(children));
}

function push(target: ProseMirrorNode[], node: ProseMirrorNode | null): void {
  if (node) target.push(node);
}

function tokenAttr(token: Token, name: string): string | null {
  const value = token.attrGet(name);
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return null;
}

function alignmentOf(token: Token): CellAlignment | null {
  const style = tokenAttr(token, "style");
  if (!style) return null;
  const match = /text-align\s*:\s*(left|center|right)/.exec(style);
  const value = match?.[1];
  if (value === "left" || value === "center" || value === "right") return value;
  return null;
}

/* ------------------------------------------------------------------ inline */

const MARK_TOKENS: Record<string, string | undefined> = {
  strong: MarkName.strong,
  em: MarkName.em,
  underline: MarkName.underline,
  s: MarkName.strikethrough,
  highlight: MarkName.highlight,
  placeholder: MarkName.placeholder,
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return `${String(day)}th`;
  const suffixes: Record<number, string | undefined> = { 1: "st", 2: "nd", 3: "rd" };
  return `${String(day)}${suffixes[day % 10] ?? "th"}`;
}

/** Date mentions show a formatted date, not whatever label the markdown held. */
export function formatMentionDate(isoDate: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return null;
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return null;
  return `${month} ${ordinal(Number(match[3]))}`;
}

/**
 * Splits a text run on the dialect's hard-break spellings. A `<br>` tag is
 * rewritten to the same marker first, an escaped backslash protects a following
 * `n`, and the text on each side of a break is trimmed.
 */
function restoreEscapes(text: string): string {
  return text.includes(ESCAPED_BACKSLASH) ? text.split(ESCAPED_BACKSLASH).join("\\") : text;
}

function splitHardBreaks(text: string): string[] {
  const marked = text.replace(/<br\s*\/?>/gi, HARD_BREAK_MARKER).replace(/\\n/g, HARD_BREAK_MARKER);
  if (!marked.includes(HARD_BREAK_MARKER)) return [restoreEscapes(text)];
  const parts = marked.split(HARD_BREAK_MARKER);
  // Only the sides that actually touch a break are trimmed.
  return parts.map((part, index) => {
    const head = index === 0 ? part : part.replace(/^\s+/, "");
    const body = index === parts.length - 1 ? head : head.replace(/\s+$/, "");
    return restoreEscapes(body);
  });
}

function textNodes(text: string, marks: readonly Mark[], out: ProseMirrorNode[]): void {
  const parts = splitHardBreaks(text);
  parts.forEach((part, index) => {
    if (index > 0) push(out, nodeType(NodeName.br).create());
    if (part !== "") out.push(schema.text(part, marks));
  });
}

export interface ImageTitleParts {
  source: string | null;
  layoutClass: ImageLayoutClass | null;
  title: string | null;
  width: number | null;
  height: number | null;
}

export function parseImageTitle(raw: string | null): ImageTitleParts {
  const result: ImageTitleParts = {
    source: null,
    layoutClass: null,
    title: null,
    width: null,
    height: null,
  };
  if (raw === null) return result;
  let rest = raw;

  const sourceMatch = /^\s*source=(\S+)\s*/.exec(rest);
  if (sourceMatch?.[1]) {
    result.source = sourceMatch[1];
    rest = rest.slice(sourceMatch[0].length);
  }

  const sizeMatch = /\s*=(\d*)x(\d*)\s*$/.exec(rest);
  if (sizeMatch && (sizeMatch[1] !== "" || sizeMatch[2] !== "")) {
    result.width = sizeMatch[1] === "" ? null : Number(sizeMatch[1]);
    result.height = sizeMatch[2] === "" ? null : Number(sizeMatch[2]);
    rest = rest.slice(0, rest.length - sizeMatch[0].length);
  }

  const remainder = rest.trim();
  for (const layout of IMAGE_LAYOUT_CLASSES) {
    if (remainder === layout) {
      result.layoutClass = layout;
      return result;
    }
  }
  if (remainder !== "") result.title = remainder;
  return result;
}

function inlineFrom(nodes: TokenNode[], marks: readonly Mark[], out: ProseMirrorNode[]): void {
  for (const node of nodes) {
    const token = node.token;
    const markName =
      token.nesting === 1 ? MARK_TOKENS[token.type.replace(/_open$/, "")] : undefined;
    if (markName) {
      inlineFrom(node.children, [...marks, markType(markName).create()], out);
      continue;
    }
    switch (token.type) {
      case "text":
        textNodes(token.content, marks, out);
        break;
      case "code_inline":
        out.push(
          schema.text(restoreEscapes(token.content), [
            ...marks,
            markType(MarkName.codeInline).create(),
          ]),
        );
        break;
      case "softbreak":
        textNodes(" ", marks, out);
        break;
      case "hardbreak":
        push(out, nodeType(NodeName.br).create());
        break;
      case "link_open": {
        const link = markType(MarkName.link).create({
          href: tokenAttr(token, "href") ?? "",
          title: tokenAttr(token, "title"),
        });
        inlineFrom(node.children, [...marks, link], out);
        break;
      }
      case "image": {
        const title = parseImageTitle(tokenAttr(token, "title"));
        push(
          out,
          nodeType(NodeName.image).create({
            src: tokenAttr(token, "src") ?? "",
            width: title.width ?? undefined,
            height: title.height ?? undefined,
            alt: token.content === "" ? null : restoreEscapes(token.content).replace(/\n/g, ""),
            source: title.source,
            layoutClass: title.layoutClass,
            title: title.title,
          }),
        );
        break;
      }
      case "math_inline":
        push(
          out,
          nodeType(NodeName.mathInline).createAndFill(
            null,
            token.content === "" ? undefined : schema.text(token.content),
          ),
        );
        break;
      case "emoji":
        push(out, nodeType(NodeName.emoji).create({ "data-name": token.content }));
        break;
      case "mention": {
        const type = (tokenAttr(token, "mentionType") ?? "user") as MentionType;
        const modelId = tokenAttr(token, "modelId") ?? "";
        // A date mention keeps the label it was written with; the visible date
        // is derived from `modelId` at serialization time.
        const label = restoreEscapes(token.content);
        const href = tokenAttr(token, "href");
        const attrs: Record<string, unknown> = {
          type,
          label,
          modelId,
          id: tokenAttr(token, "mentionId") ?? "",
        };
        if (href !== null) attrs.href = href;
        push(out, nodeType(NodeName.mention).create(attrs));
        break;
      }
      default:
        if (node.children.length > 0) inlineFrom(node.children, marks, out);
        break;
    }
  }
}

/* ------------------------------------------------------------------ blocks */

function countHardBreaks(children: ProseMirrorNode[]): number {
  return children.filter((child) => child.type.name === NodeName.br).length;
}

/**
 * A paragraph whose whole content is lone backslashes (and the hard breaks
 * between them) stands for that many empty paragraphs.
 */
function countBackslashParagraphs(children: ProseMirrorNode[]): number | null {
  let backslashes = 0;
  for (const child of children) {
    if (child.type.name === NodeName.br) continue;
    if (child.isText && child.text === "\\" && child.marks.length === 0) {
      backslashes += 1;
      continue;
    }
    return null;
  }
  return backslashes > 0 ? backslashes + countHardBreaks(children) : null;
}

/** A paragraph holding exactly one qualifying link becomes a file card. */
function fileCard(children: ProseMirrorNode[]): ProseMirrorNode | null {
  if (children.length !== 1) return null;
  const only = children[0];
  if (!only?.isText || only.text === undefined) return null;
  if (only.marks.length !== 1) return null;
  const link = only.marks[0];
  if (link?.type.name !== MarkName.link) return null;
  const href = typeof link.attrs.href === "string" ? link.attrs.href : "";
  if (!isFileHref(href)) return null;
  const parts = parseLinkText(only.text);
  if (parts.width !== null && parts.height !== null) {
    return nodeType(NodeName.video).create({
      src: href,
      width: parts.width,
      height: parts.height,
      title: parts.title,
    });
  }
  if (parts.size !== null) {
    // `size` is a string in the public shape; it may exceed a safe integer.
    return nodeType(NodeName.attachment).create({
      href,
      title: parts.title,
      size: String(parts.size),
    });
  }
  return null;
}

function paragraphsFrom(children: ProseMirrorNode[], out: ProseMirrorNode[]): void {
  const empties = countBackslashParagraphs(children);
  if (empties !== null) {
    for (let i = 0; i < empties; i += 1) push(out, build(NodeName.paragraph, null, []));
    return;
  }
  const card = fileCard(children);
  if (card) {
    out.push(card);
    return;
  }
  push(out, build(NodeName.paragraph, null, children));
}

function listStyleFor(token: Token, context: Context): AlphaListMarker | null {
  const line = token.map?.[0];
  if (line === undefined) return null;
  return context.alphaMarkers.get(line) ?? null;
}

/** Removes the `[x] ` prefix from a task item and reports its checked state. */
function stripTaskMarker(item: TokenNode): boolean | null {
  const paragraph = item.children.find((child) => child.token.type === "paragraph_open");
  const inline = paragraph?.children.find((child) => child.token.type === "inline");
  if (!inline) return null;
  const match = TASK_MARKER.exec(inline.token.content);
  if (!match) return null;
  inline.token.content = inline.token.content.slice(match[0].length);
  const first = inline.children[0];
  if (first?.token.type === "text") {
    first.token.content = first.token.content.slice(match[0].length);
  }
  const marker = match[1] ?? " ";
  return marker === "x" || marker === "X";
}

function inlineContentOf(node: TokenNode): ProseMirrorNode[] {
  const inline: ProseMirrorNode[] = [];
  for (const child of node.children) {
    if (child.token.type === "inline") inlineFrom(child.children, [], inline);
  }
  return inline;
}

function noticeStyleOf(token: Token): string {
  const info = token.info.trim();
  if (info === "") return "info";
  return NOTICE_STYLES.includes(info as NoticeStyle) ? info : info;
}

function tableFrom(node: TokenNode): ProseMirrorNode | null {
  const rows: ProseMirrorNode[] = [];
  const collect = (group: TokenNode[]): void => {
    for (const child of group) {
      if (child.token.type === "thead_open" || child.token.type === "tbody_open") {
        collect(child.children);
        continue;
      }
      if (child.token.type !== "tr_open") continue;
      const cells: ProseMirrorNode[] = [];
      for (const cell of child.children) {
        const isHeader = cell.token.type === "th_open";
        if (!isHeader && cell.token.type !== "td_open") continue;
        const paragraph = build(NodeName.paragraph, null, inlineContentOf(cell));
        push(
          cells,
          build(
            isHeader ? NodeName.tableHeader : NodeName.tableCell,
            { alignment: alignmentOf(cell.token) },
            paragraph ? [paragraph] : [],
          ),
        );
      }
      push(rows, build(NodeName.tableRow, null, cells));
    }
  };
  collect(node.children);
  return build(NodeName.table, null, rows);
}

function blocksFrom(nodes: TokenNode[], context: Context): ProseMirrorNode[] {
  const out: ProseMirrorNode[] = [];
  for (const node of nodes) {
    const token = node.token;
    switch (token.type) {
      case "paragraph_open":
        paragraphsFrom(inlineContentOf(node), out);
        break;
      case "heading_open":
        push(
          out,
          build(NodeName.heading, { level: Number(token.tag.slice(1)) }, inlineContentOf(node)),
        );
        break;
      case "blockquote_open":
        push(out, build(NodeName.blockquote, null, blocksFrom(node.children, context)));
        break;
      case "bullet_list_open":
      case "ordered_list_open": {
        const items = node.children.filter((child) => child.token.type === "list_item_open");
        if (context.checkboxLists.has(token)) {
          const listItems: ProseMirrorNode[] = [];
          for (const item of items) {
            const checked = stripTaskMarker(item) ?? false;
            push(
              listItems,
              build(NodeName.checkboxItem, { checked }, blocksFrom(item.children, context)),
            );
          }
          push(out, build(NodeName.checkboxList, null, listItems));
          break;
        }
        const listItems: ProseMirrorNode[] = [];
        for (const item of items) {
          push(listItems, build(NodeName.listItem, null, blocksFrom(item.children, context)));
        }
        if (token.type === "bullet_list_open") {
          push(out, build(NodeName.bulletList, null, listItems));
          break;
        }
        const alpha = listStyleFor(token, context);
        const start = Number(token.attrGet("start") ?? 1);
        push(
          out,
          build(
            NodeName.orderedList,
            {
              order: alpha?.order ?? (Number.isFinite(start) ? start : 1),
              listStyle: alpha?.style ?? "number",
            },
            listItems,
          ),
        );
        break;
      }
      case "fence":
      case "code_block": {
        const language = token.info.replace(/`/g, "").trim().split(/\s+/)[0] ?? "";
        const content = token.content.replace(/\n$/, "");
        push(
          out,
          nodeType(NodeName.codeBlock).createAndFill(
            { language },
            content === "" ? undefined : schema.text(content),
          ),
        );
        break;
      }
      case "math_block":
        push(
          out,
          nodeType(NodeName.mathBlock).createAndFill(
            null,
            token.content === "" ? undefined : schema.text(token.content),
          ),
        );
        break;
      case "hr":
        push(out, nodeType(NodeName.hr).create({ markup: token.markup || "---" }));
        break;
      case "notice_open":
        push(
          out,
          build(
            NodeName.containerNotice,
            { style: noticeStyleOf(token) },
            blocksFrom(node.children, context),
          ),
        );
        break;
      case "toggle_open":
        // The fence's info string is discarded. The first block is the
        // toggle's title; when it cannot be one, an empty paragraph is filled
        // in and the offending block is dropped.
        push(out, build(NodeName.containerToggle, null, blocksFrom(node.children, context)));
        break;
      case "table_open":
        push(out, tableFrom(node));
        break;
      default:
        if (node.children.length > 0) out.push(...blocksFrom(node.children, context));
        break;
    }
  }
  return out;
}

/* --------------------------------------------------------------------- API */

export function parseMarkdown(text: string): ProseMirrorNode {
  const { source, alphaMarkers } = preprocessAlphaLists(text);
  const tokens = parser.parse(source, {});
  const checkboxLists = new Set<Token>();
  for (const index of claimTaskLists(tokens)) {
    const token = tokens[index];
    if (token) checkboxLists.add(token);
  }
  const blocks = blocksFrom(buildTree(tokens), { alphaMarkers, checkboxLists });
  const doc = nodeType(NodeName.doc).createAndFill(null, Fragment.from(blocks));
  if (!doc) throw new Error("Failed to build a document from the parsed markdown");
  return doc;
}

export function parseMarkdownToJson(text: string): Record<string, unknown> {
  return parseMarkdown(text).toJSON() as Record<string, unknown>;
}
